import FileDownloadIcon from "@mui/icons-material/FileDownload";
import Button from "@mui/material/Button";
import { showErrorSnackbar } from "_shared/_errors/error-snackbar";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import type { AppDispatch } from "_shared/_stores/store-types";
import type { PermissionKind } from "permissions/_api/permission-kinds-api-service";
import {
  grantExportCsv,
  grantExportFileName,
  grantExportRow,
  sortGrantExportRows,
} from "permissions/_views/export/grant-export-model";
import { type GrantExportScope, readGrantExport } from "permissions/_views/export/grant-export-reads";
import { grantExport } from "permissions/_views/wording/permission-wording";
import { useState } from "react";
import { useDispatch } from "react-redux";

/**
 * **Export CSV** in the header of the person, record and kind pages (#6786): reads every page of the
 * grants the page lists, names them as the rows do, and saves one line per grant. A button on the
 * record and kind pages, and for a reader who may change nothing; an item of the person page's menu
 * otherwise, where three buttons already share the line.
 */

/** Saves `text` as a CSV file named `fileName`. */
export function saveCsv(text: string, fileName: string): void {
  const url = globalThis.URL.createObjectURL(new Blob([text], { type: "text/csv;charset=utf-8" }));
  const link = document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute("download", fileName);
  link.click();
  // A named download reads the URL while the click is handled, so it can go at once.
  globalThis.URL.revokeObjectURL(url);
}

type Progress = Readonly<{ read: number; total?: number }>;

/**
 * The export from start to file. At module level because a `try` holding conditionals makes the React
 * compiler skip the whole component.
 */
async function exportGrants(
  dispatch: AppDispatch,
  scope: GrantExportScope,
  kinds: readonly PermissionKind[],
  options: Readonly<{ subject: string; onProgress: (progress: Progress) => void }>,
): Promise<void> {
  try {
    const { grants, names } = await dispatch(
      readGrantExport(scope, kinds, (read, total) => options.onProgress({ read, total })),
    );
    const rows = grants.map((grant) => grantExportRow(grant, kinds, names));
    saveCsv(grantExportCsv(sortGrantExportRows(rows, kinds)), grantExportFileName(options.subject));
    enqueueSnackbar(grantExport.done(grants.length), { variant: "success", role: "status" });
  } catch (error) {
    showErrorSnackbar(error, { doing: grantExport.doing, operation: "permissions.grants.export" });
  }
}

export type GrantExportProps = Readonly<{
  scope: GrantExportScope;
  kinds: readonly PermissionKind[];
  /** What the file is named after: the person, the record or the kind. */
  subject: string;
}>;

export type GrantExportAction = Readonly<{
  /** "Export CSV", or while the grants are read, "Exporting… 200 of 587". */
  label: string;
  /** An export is under way; one runs at a time. */
  busy: boolean;
  start: () => void;
}>;

/** The export as an action: its label, whether one is running, and what starts it. */
export function useGrantExport(props: GrantExportProps): GrantExportAction {
  const dispatch = useDispatch<AppDispatch>();
  const [progress, setProgress] = useState<Progress>();

  const run = async () => {
    setProgress({ read: 0 });
    await exportGrants(dispatch, props.scope, props.kinds, { subject: props.subject, onProgress: setProgress });
    setProgress(undefined);
  };

  return {
    label: progress ? grantExport.progress(progress.read, progress.total) : grantExport.button,
    busy: progress !== undefined,
    start: () => void run(),
  };
}

/** The export as an outlined header button, for a page that runs it through {@link useGrantExport}. */
export function GrantExportButton(props: Readonly<{ action: GrantExportAction }>) {
  const { action } = props;
  return (
    <Button variant="outlined" startIcon={<FileDownloadIcon />} disabled={action.busy} onClick={action.start}>
      {action.label}
    </Button>
  );
}

export default function ExportGrantsButton(props: GrantExportProps) {
  return <GrantExportButton action={useGrantExport(props)} />;
}
